import React, { useState } from 'react'
import {
  CAlert, CButton, CSpinner, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle,
} from '@coreui/react'
import { api } from '../api/client'
import { abrirCheckoutEpayco } from '../utils/epayco'
import SubirPagoModal from './SubirPagoModal'
import type { ModuloRutaPago } from './SubirPagoModal'

/**
 * ModalPagoModulo — activación de un módulo de recibo POR RUTA con las 3
 * opciones SIEMPRE visibles (pedido Rafael 2026-09-28): pagar con PayPal,
 * pagar con ePayco o subir el comprobante (transferencia/giro). El pago queda
 * ASIGNADO A ESA RUTA: la pasarela enciende el módulo al activar
 * (PagoController.ActivarAsync: casos "activar-…") y el comprobante lo enciende
 * cuando el superadmin lo confirma (PagoService.ModificarPagoAsync).
 * Reutiliza el modal COMPARTIDO SubirPagoModal con moduloRuta. [RGM]
 */
interface ModalPagoModuloProps {
  visible: boolean
  onClose: () => void
  tipo: ModuloRutaPago['tipo']
  zonaCodigo: number
  /** Texto del título (ej. "Tickets de impresión — Ruta Centro"). */
  etiqueta?: string
  /** Se invoca tras subir un comprobante con éxito (el padre refresca). */
  onPagado?: () => void
}

const ModalPagoModulo: React.FC<ModalPagoModuloProps> = ({
  visible, onClose, tipo, zonaCodigo, etiqueta, onPagado,
}) => {
  const [pagando, setPagando] = useState<'' | 'paypal' | 'epayco'>('')
  const [error, setError] = useState('')
  const [subirAbierto, setSubirAbierto] = useState(false)

  // PayPal: igual que RutasPage.pagarModulo — la orden en USD se crea en el
  // backend y el contexto queda en sessionStorage para capturarla al volver
  // (?paypal=ok → PagoPage). [RGM]
  // [RGM] 2026-09-29 (pedido Rafael: "debería mostrarse un círculo cargando
  // en el botón mientras abre la página"): `pagando` marca QUÉ botón está en
  // curso y muestra el spinner hasta que la pasarela abre.
  const pagarPaypal = async () => {
    if (pagando) return
    setError('')
    setPagando('paypal')
    sessionStorage.setItem('pagoCtx', JSON.stringify({ tipo, zonaCodigo }))
    try {
      const r = await api.post<{ approvalUrl?: string }>('/pago/paypal/orden', { tipo, zonaCodigo })
      if (r.approvalUrl) {
        window.location.href = r.approvalUrl
        return
      }
      sessionStorage.removeItem('pagoCtx')
      setError('No se pudo iniciar el pago con PayPal.')
    } catch (err: any) {
      sessionStorage.removeItem('pagoCtx')
      setError(err?.message ?? 'No se pudo iniciar el pago.')
    } finally {
      setPagando('')
    }
  }

  // ePayco: mismo flujo de CuentaPage.pagarMensualidad (checkout v2: el backend
  // crea la sesión y el widget se abre con abrirCheckoutEpayco). La activación
  // la hace el webhook de confirmación. [RGM]
  const pagarEpayco = async () => {
    if (pagando) return
    setError('')
    setPagando('epayco')
    sessionStorage.setItem('pagoCtx', JSON.stringify({ tipo, zonaCodigo }))
    try {
      const r = await api.post<{ sessionId?: string; test?: boolean }>(
        '/pago/epayco/orden', { tipo, zonaCodigo })
      if (!r.sessionId) {
        setError('No se pudo iniciar el pago con ePayco.')
        return
      }
      await abrirCheckoutEpayco(r.sessionId, !!r.test)
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo iniciar el pago.')
    } finally {
      setPagando('')
    }
  }

  // Cierra ambas capas (opciones y subir comprobante) y avisa al padre.
  const cerrar = () => {
    setError('')
    setSubirAbierto(false)
    onClose()
  }

  // [RGM] 2026-09-29 (reporte Rafael: "elijo subir pago pero no sale el
  // formulario"): en CModal 5.13 `onClose` dispara en TODA salida del modal
  // (onExit del Transition), incluso al ocultar el de opciones para abrir el
  // formulario — este guard evita cerrar todo en ese caso; solo cierra cuando
  // el usuario descarta las opciones (sin formulario abierto).
  const cerrarOpciones = () => {
    if (!subirAbierto) cerrar()
  }

  return (
    <>
      <CModal visible={visible && !subirAbierto} onClose={cerrarOpciones}>
        <CModalHeader>
          <CModalTitle>{etiqueta ?? 'Activar módulo'}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {error && <CAlert color="danger" className="py-2">{error}</CAlert>}
          <div className="d-flex flex-column gap-2">
            <CButton color="primary" type="button" disabled={!!pagando} onClick={pagarPaypal}>
              {pagando === 'paypal' && <CSpinner size="sm" className="me-2" />}
              {pagando === 'paypal' ? 'Abriendo PayPal…' : 'Pagar con PayPal'}
            </CButton>
            <CButton color="primary" variant="outline" type="button" disabled={!!pagando} onClick={pagarEpayco}>
              {pagando === 'epayco' && <CSpinner size="sm" className="me-2" />}
              {pagando === 'epayco' ? 'Abriendo ePayco…' : 'Pagar con ePayco'}
            </CButton>
            {/* [RGM] 2026-09-29 (pedido Rafael): "Subir pago" del módulo por
                ruta manda el tipo de pago (6 = Tickets de Impresión solo;
                3 = Impresora - WhatsApp) — el SubirPagoModal lo fija y lo
                muestra deshabilitado. */}
            <CButton color="secondary" variant="outline" type="button" disabled={!!pagando}
              onClick={() => setSubirAbierto(true)}>
              Subir pago
            </CButton>
          </div>
          <div className="text-body-secondary mt-3" style={{ fontSize: 12 }}>
            El pago queda asignado a esta ruta. Si subes el comprobante (transferencia o giro),
            el módulo se activa cuando el administrador confirme el pago.
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="ghost" type="button" onClick={cerrar}>
            Cancelar
          </CButton>
        </CModalFooter>
      </CModal>
      <SubirPagoModal
        visible={subirAbierto}
        onClose={cerrar}
        moduloRuta={{ tipo, zonaCodigo }}
        onSubido={() => onPagado?.()}
      />
    </>
  )
}

export default ModalPagoModulo
